// Composite-lens content uploads (Lenses v1.1).
//
// A composite lens declares per-run content inputs (`lens.inputs` in the
// prose frontmatter — e.g. the screenshot placed on a device screen). The
// client posts the image as base64 via `lenses_upload_request`; this module
// validates it and saves it under `<lens-dir>/uploads/` — a SIBLING of
// `scratch/` and `renders/` — with a generated filename that is returned as
// the `uploadId`. The render request later references uploads by id only;
// the daemon never accepts client-supplied paths.
//
// Validation is deliberately strict (default-deny mindset, same as the
// client-scope allow-matrix):
//   - lens must exist and declare the input key in `lens.inputs`
//   - bytes must sniff as PNG / JPEG / WebP (magic bytes, not just mimeType)
//   - size cap 6 MiB binary (≈8 MB base64 on the wire — comfortably inside
//     what the relay already carries for render-history replies)

import { mkdir, readFile, writeFile } from "node:fs/promises";
import { randomBytes } from "node:crypto";
import { join } from "path";
import { parse as parseYaml } from "yaml";
import type pino from "pino";
import { resolveLensDir } from "./paths.js";

/** Binary size cap for one uploaded content image. */
export const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;

export interface SaveLensUploadArgs {
  cwd: string;
  lensSlug: string;
  inputKey: string;
  mimeType: string;
  /** Base64 image bytes (no data-URI prefix). */
  data: string;
  logger: pino.Logger;
}

export interface SaveLensUploadResult {
  ok: boolean;
  uploadId?: string;
  error?: string;
  errorCode?: string;
}

interface SniffedImage {
  ext: "png" | "jpg" | "webp";
  mime: string;
}

/** Magic-byte sniff — the client's mimeType is a hint, never trusted. */
export function sniffImageBytes(bytes: Buffer): SniffedImage | null {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return { ext: "png", mime: "image/png" };
  }
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return { ext: "jpg", mime: "image/jpeg" };
  }
  if (
    bytes.length >= 12 &&
    bytes.toString("ascii", 0, 4) === "RIFF" &&
    bytes.toString("ascii", 8, 12) === "WEBP"
  ) {
    return { ext: "webp", mime: "image/webp" };
  }
  return null;
}

/**
 * uploadId grammar. Generated server-side; the render path re-validates any
 * uploadId it receives against this before touching the filesystem, so a
 * crafted id can never traverse out of `<lens-dir>/uploads/`.
 */
const UPLOAD_ID_RE = /^upload-\d{8}-[a-f0-9]{8}\.(png|jpg|webp)$/;

export function isValidUploadId(id: string): boolean {
  return UPLOAD_ID_RE.test(id);
}

export function uploadsDir(cwd: string, lensSlug: string): string {
  return join(resolveLensDir(cwd, lensSlug), "uploads");
}

interface LensInputSpec {
  key?: unknown;
  required?: unknown;
}

/** Reads the lens prose and returns its declared `lens.inputs` (or []). */
export async function readLensInputSpecs(
  cwd: string,
  lensSlug: string,
): Promise<Array<{ key: string; required: boolean }> | null> {
  const prosePath = join(resolveLensDir(cwd, lensSlug), "prose.md");
  let raw: string;
  try {
    raw = await readFile(prosePath, "utf8");
  } catch {
    return null; // lens not found
  }
  if (!raw.startsWith("---")) return [];
  const end = raw.indexOf("\n---", 3);
  if (end === -1) return [];
  try {
    const fm = parseYaml(raw.slice(raw.indexOf("\n") + 1, end)) as {
      lens?: { inputs?: unknown };
    } | null;
    const inputs = fm?.lens?.inputs;
    if (!Array.isArray(inputs)) return [];
    return inputs
      .filter((i): i is LensInputSpec => typeof i === "object" && i !== null)
      .filter((i) => typeof i.key === "string")
      .map((i) => ({ key: i.key as string, required: i.required === true }));
  } catch {
    return [];
  }
}

export async function saveLensUpload(args: SaveLensUploadArgs): Promise<SaveLensUploadResult> {
  const { cwd, lensSlug, inputKey, data, logger } = args;
  const log = logger.child({ component: "lens-uploads", lensSlug, inputKey });

  const specs = await readLensInputSpecs(cwd, lensSlug);
  if (specs === null) {
    return { ok: false, error: "lens file not found", errorCode: "lens_not_found" };
  }
  if (!specs.some((s) => s.key === inputKey)) {
    return {
      ok: false,
      error: `lens does not declare input "${inputKey}"`,
      errorCode: "upload_input_unknown",
    };
  }

  let bytes: Buffer;
  try {
    bytes = Buffer.from(data, "base64");
  } catch {
    return { ok: false, error: "invalid base64 payload", errorCode: "upload_invalid" };
  }
  if (bytes.length === 0) {
    return { ok: false, error: "empty upload", errorCode: "upload_invalid" };
  }
  if (bytes.length > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      error: `upload exceeds ${Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024))} MiB cap`,
      errorCode: "upload_too_large",
    };
  }
  const sniffed = sniffImageBytes(bytes);
  if (!sniffed) {
    return {
      ok: false,
      error: "unsupported image type (need PNG, JPEG, or WebP)",
      errorCode: "upload_unsupported_type",
    };
  }

  const d = new Date();
  const stamp = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
  const uploadId = `upload-${stamp}-${randomBytes(4).toString("hex")}.${sniffed.ext}`;
  const dir = uploadsDir(cwd, lensSlug);
  try {
    await mkdir(dir, { recursive: true });
    await writeFile(join(dir, uploadId), bytes);
  } catch (err) {
    log.error({ err }, "failed to persist lens upload");
    return { ok: false, error: "failed to save upload", errorCode: "upload_write_failed" };
  }

  log.info({ uploadId, bytes: bytes.length }, "lens content upload saved");
  return { ok: true, uploadId };
}
